// src/pages/Token/TokenPage.jsx

import React, { useEffect, useState } from "react";
import { Edit, Trash2, PlusCircle, Search, WalletCardsIcon, XCircle, CheckCircle } from "lucide-react";
import { Drawer, Button, Spin, Popconfirm, Pagination, Switch, message } from "antd";
import { motion, AnimatePresence } from "framer-motion";
import tokenApi from "../../api/tokenApi";

import bsc from "../../assets/icons/blockchains/BNB.png";
import ethereum from "../../assets/icons/blockchains/ETH.png";
import solana from "../../assets/icons/blockchains/solana.png";
import sui from "../../assets/icons/blockchains/sui.svg";
import ton from "../../assets/icons/blockchains/TON.png";
import polygon from "../../assets/icons/blockchains/polygon.svg";
import avalanche from "../../assets/icons/blockchains/avalanche.svg";
import optimism from "../../assets/icons/blockchains/optimism.svg";
import arbitrum from "../../assets/icons/blockchains/arbitrum.svg";
import base from "../../assets/icons/blockchains/base.svg";
import TokenCardOverlay from "./TokenModal";

const MotionButton = motion(Button);

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const inputClass =
    "w-full rounded-xl border border-white/10 bg-white/[0.06] px-3 py-2.5 text-[13px] text-white/90 outline-none transition placeholder:text-white/35 focus:border-emerald-400/45 focus:ring-2 focus:ring-emerald-500/15";

export default function TokenPage() {
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalTokens, setTotalTokens] = useState(0);

    const [useFilters, setUseFilters] = useState(false);
    const [addrInput, setAddrInput] = useState("");
    const [selectedFilterBlockchain, setSelectedFilterBlockchain] = useState("");
    const [isVisible, setIsVisible] = useState(false);
    const [foundTokenInDb, setFoundTokenInDb] = useState(null);
    const [searchInDbLoading, setSearchInDbLoading] = useState(false);
    const [searchInDbAttempted, setSearchInDbAttempted] = useState(false);

    const [lookupExternalBlockchain, setLookupExternalBlockchain] = useState("");
    const [lookupResult, setLookupResult] = useState(null);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupAttempted, setLookupAttempted] = useState(false);

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editingToken, setEditingToken] = useState(null);
    const [manualForm, setManualForm] = useState({
        name: "",
        symbol: "",
        address: "",
        blockchain: "",
        file: null,
    });

    const supportedChains = [
        "hardhat",
        "ethereum",
        "bsc",
        "solana",
        "sui",
        "base",
        "ton",
        "polygon",
        "optimism",
        "arbitrum",
        "avalanche",
    ];
    const chainIcons = {
        ethereum,
        bsc,
        solana,
        base,
        sui,
        ton,
        polygon,
        optimism,
        arbitrum,
        avalanche,
        hardhat: null, // специальный рендер через иконку-текст
    };

    useEffect(() => {
        fetchTokens(currentPage, pageSize, selectedFilterBlockchain);
    }, [currentPage, pageSize, selectedFilterBlockchain]);

    async function fetchTokens(page, limit, blockchain = "") {
        setLoading(true);
        try {
            const data = blockchain
                ? await tokenApi.getTokensByBlockchain(blockchain, page, limit)
                : await tokenApi.getTokens(page, limit);
            setTokens(data.items);
            setTotalTokens(data.total);
        } catch (e) {
            console.error("Ошибка загрузки токенов", e);
            message.error("Не удалось загрузить токены");
            setTokens([]);
            setTotalTokens(0);
        } finally {
            setLoading(false);
        }
    }

    async function handleDelete(id) {
        try {
            await tokenApi.deleteToken(id);
            message.success("Токен удалён");
            fetchTokens(currentPage, pageSize, selectedFilterBlockchain);
        } catch (e) {
            console.error("Ошибка при удалении токена:", e);
            message.error("Не удалось удалить токен");
        }
    }

    async function handleMainButton() {
        if (useFilters) {
            if (addrInput.trim()) {
                setSearchInDbLoading(true);
                setSearchInDbAttempted(false);
                setFoundTokenInDb(null);
                try {
                    const res = await tokenApi.getTokenByAddress(addrInput.trim());
                    setFoundTokenInDb(res);
                } catch {
                    setFoundTokenInDb(null);
                } finally {
                    setSearchInDbLoading(false);
                    setSearchInDbAttempted(true);
                    setIsVisible(true);
                }
            }
        } else {
            if (!addrInput.trim() || !lookupExternalBlockchain) return;

            setLookupLoading(true);
            setLookupAttempted(false);
            setLookupResult(null);

            try {
                const res = await tokenApi.lookupToken(addrInput.trim(), lookupExternalBlockchain);
                setLookupResult(res);
                setCurrentPage(1);
                await fetchTokens(1, pageSize, selectedFilterBlockchain);
            } catch {
                setLookupResult(null);
            } finally {
                setLookupLoading(false);
                setLookupAttempted(true);
            }
        }
    }

    function openDrawerForCreate() {
        setEditingToken(null);
        setManualForm({ name: "", symbol: "", address: "", blockchain: "", file: null });
        setIsDrawerOpen(true);
    }
    function openDrawerForEdit(token) {
        setEditingToken(token);
        setManualForm({
            name: token.name,
            symbol: token.symbol,
            address: token.address,
            blockchain: token.blockchain,
            file: null,
        });
        setIsDrawerOpen(true);
    }
    function handleManualChange(field, val) {
        setManualForm((prev) => ({ ...prev, [field]: val }));
    }
    function handleFileChange(e) {
        setManualForm((prev) => ({ ...prev, file: e.target.files[0] }));
    }
    async function handleManualSubmit() {
        const { name, symbol, address, blockchain, file } = manualForm;
        if (!name || !symbol || !address || !blockchain || (!editingToken && !file)) {
            message.warning("Заполните все поля и выберите файл при создании.");
            return;
        }
        try {
            if (editingToken) {
                await tokenApi.updateToken(editingToken.id, {
                    name,
                    symbol,
                    address: address.trim(),
                    blockchain,
                });
                message.success("Токен обновлён");
            } else {
                const fd = new FormData();
                fd.append("name", name);
                fd.append("symbol", symbol);
                fd.append("address", address.trim());
                fd.append("blockchain", blockchain);
                fd.append("file", file);
                await tokenApi.createManualToken(fd);
                message.success("Токен создан");
            }
            setIsDrawerOpen(false);
            fetchTokens(currentPage, pageSize, selectedFilterBlockchain);
        } catch (e) {
            console.error("Ошибка при сохранении:", e);
            message.error("Не удалось сохранить");
        }
    }

    const CHAIN_BADGE = {
        hardhat: { bg: '#F7C520', text: 'HH' },
    };

    const chainPill = (chain, isSel, onClick) => {
        const badge = CHAIN_BADGE[chain];
        return (
            <button
                type="button"
                onClick={onClick}
                className={cn(
                    "inline-flex cursor-pointer select-none items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors",
                    isSel
                        ? "border-emerald-400/45 bg-emerald-500/20 text-emerald-100 shadow-[0_0_20px_-8px_rgba(52,211,153,0.5)]"
                        : "border-white/10 bg-white/[0.06] text-white/65 hover:border-white/18 hover:bg-white/10 hover:text-white/85"
                )}
            >
                {badge ? (
                    <span
                        className="flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px] font-bold leading-none"
                        style={{ background: badge.bg, color: '#000' }}
                    >
                        {badge.text}
                    </span>
                ) : (
                    <img src={chainIcons[chain]} alt="" className="h-3.5 w-3.5 object-contain" />
                )}
                {chain[0].toUpperCase() + chain.slice(1)}
            </button>
        );
    };

    const totalPages = Math.max(1, Math.ceil(totalTokens / pageSize) || 1);
    const th = "whitespace-nowrap px-3 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wide text-white/50 sm:px-4";
    const td = "px-3 py-3 text-[13px] text-white/85 sm:px-4";

    if (loading && !tokens.length && totalTokens === 0) {
        return (
            <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
                <section className={cn(shellClass, "flex min-h-[280px] flex-1 items-center justify-center p-12")}>
                    <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                </section>
            </div>
        );
    }

    return (
        <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 overflow-hidden p-4 text-white sm:p-5">
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-emerald-500/10 blur-3xl" />
                <div className="relative flex items-center gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                        <WalletCardsIcon className="h-5 w-5 text-emerald-300/90" strokeWidth={2} />
                    </span>
                    <div>
                        <h1 className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">
                            Управление токенами
                        </h1>
                        <p className="text-[12px] text-white/45">Поиск в БД, внешний lookup, сети и каталог</p>
                    </div>
                </div>
            </section>

            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:gap-4">
                        <input
                            type="text"
                            className={cn(inputClass, "min-w-0 flex-1 xl:max-w-md")}
                            placeholder="Введите адрес контракта…"
                            value={addrInput}
                            onChange={(e) => {
                                setAddrInput(e.target.value);
                                if (useFilters) {
                                    setSearchInDbAttempted(false);
                                    setFoundTokenInDb(null);
                                } else {
                                    setLookupAttempted(false);
                                    setLookupResult(null);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleMainButton();
                            }}
                        />
                        <div className="flex flex-wrap gap-1.5">
                            {supportedChains.map((chain) => {
                                const isSel = useFilters
                                    ? chain === selectedFilterBlockchain
                                    : chain === lookupExternalBlockchain;
                                return (
                                    <React.Fragment key={chain}>
                                        {chainPill(chain, isSel, () => {
                                            if (useFilters) {
                                                setSelectedFilterBlockchain(isSel ? "" : chain);
                                                setCurrentPage(1);
                                            } else {
                                                setLookupExternalBlockchain(isSel ? "" : chain);
                                                setLookupAttempted(false);
                                                setLookupResult(null);
                                            }
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-wrap items-center gap-3">
                            <Switch
                                checked={useFilters}
                                onChange={(checked) => {
                                    setUseFilters(checked);
                                    if (!checked) {
                                        setSelectedFilterBlockchain("");
                                        setCurrentPage(1);
                                    }
                                }}
                                checkedChildren="Filters"
                                unCheckedChildren="Lookup"
                                className="[&_.ant-switch-checked]:!bg-emerald-500"
                            />
                            <span className="text-[12px] text-white/40">
                                {useFilters ? "Фильтр таблицы и поиск в БД" : "Внешний lookup по сети"}
                            </span>
                        </div>
                        <MotionButton
                            onClick={handleMainButton}
                            type="primary"
                            icon={<Search size={16} />}
                            loading={useFilters ? searchInDbLoading : lookupLoading}
                            className="!rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {useFilters ? "Найти в БД" : "Lookup"}
                        </MotionButton>
                    </div>

                    <div className="min-h-[1.5rem] text-[13px]">
                        {useFilters ? (
                            !searchInDbLoading &&
                            searchInDbAttempted &&
                            !foundTokenInDb && (
                                <div className="flex items-center gap-2 text-rose-300/95">
                                    <XCircle size={16} />
                                    В БД не найдено
                                </div>
                            )
                        ) : (
                            <>
                                {!lookupLoading && lookupAttempted && lookupResult && (
                                    <div className="flex items-center gap-2 text-emerald-300/95">
                                        <CheckCircle size={16} />
                                        {lookupResult.name} ({lookupResult.symbol})
                                    </div>
                                )}
                                {!lookupLoading && lookupAttempted && !lookupResult && (
                                    <div className="flex items-center gap-2 text-rose-300/95">
                                        <XCircle size={16} />
                                        Во внешних API не найдено
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </section>

            {totalTokens > 0 && (
                <div
                    className={cn(
                        shellClass,
                        "flex shrink-0 flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5"
                    )}
                >
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                        <span className="text-[13px] text-white/50">
                            Стр. {currentPage} из {totalPages}
                        </span>
                        <span className="text-[12px] text-white/35">
                            Токенов: {totalTokens} · на странице: {pageSize}
                        </span>
                    </div>
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={totalTokens}
                        showSizeChanger
                        pageSizeOptions={["10", "20", "50"]}
                        onChange={(p, sz) => {
                            setCurrentPage(p);
                            setPageSize(sz);
                        }}
                        className="pagination-custom !mb-0 flex flex-wrap justify-end"
                        size="small"
                    />
                </div>
            )}

            <section className={cn(shellClass, "relative flex min-h-0 flex-1 flex-col overflow-hidden")}>
                {loading && tokens.length > 0 && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-[#070A12]/45 backdrop-blur-sm">
                        <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                    </div>
                )}
                <div className="flex shrink-0 flex-col gap-3 border-b border-white/10 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
                    <h2 className="flex items-center gap-2 text-[15px] font-semibold text-white/90">
                        <PlusCircle size={18} className="text-emerald-300/90" />
                        Загруженные токены
                    </h2>
                    <Button
                        type="default"
                        onClick={openDrawerForCreate}
                        icon={<PlusCircle size={16} />}
                        className="!rounded-xl !border-white/10 !bg-white/[0.06] !text-white/85 hover:!border-white/20 hover:!bg-white/10"
                    >
                        Добавить токен
                    </Button>
                </div>
                <div className="min-h-0 flex-1 overflow-auto p-3 sm:p-4">
                    <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
                        <table className="w-full min-w-[880px] border-collapse text-left">
                            <thead className="sticky top-0 z-[1] bg-[#0a0e18]/95 text-white/70 backdrop-blur-md">
                                <tr className="border-b border-white/10">
                                    <th className={th}>Логотип</th>
                                    <th className={th}>Name</th>
                                    <th className={th}>Symbol</th>
                                    <th className={th}>Address</th>
                                    <th className={th}>Blockchain</th>
                                    <th className={th}>Источник</th>
                                    <th className={cn(th, "text-right")}>Действия</th>
                                </tr>
                            </thead>
                            <AnimatePresence>
                                <tbody>
                                    {tokens.length === 0 ? (
                                        <tr>
                                            <td colSpan={7} className="px-4 py-16 text-center text-[13px] text-white/45">
                                                Нет токенов для отображения.
                                            </td>
                                        </tr>
                                    ) : (
                                        tokens.map((token) => (
                                            <motion.tr
                                                key={token.id}
                                                className="border-t border-white/[0.06] transition-colors hover:bg-white/[0.04]"
                                                initial={{ opacity: 0, y: 8 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -8 }}
                                                transition={{ duration: 0.18 }}
                                            >
                                                <td className={td}>
                                                    <img
                                                        src={token.logoUrl}
                                                        alt=""
                                                        className="h-8 w-8 rounded-full border border-white/10 object-cover"
                                                    />
                                                </td>
                                                <td className={cn(td, "font-medium text-white/90")}>{token.name}</td>
                                                <td className={td}>
                                                    <span className="inline-flex rounded-full border border-white/12 bg-white/[0.06] px-2 py-0.5 text-[11px] text-white/75">
                                                        {token.symbol}
                                                    </span>
                                                </td>
                                                <td className={cn(td, "max-w-[200px] truncate font-mono text-[11px] text-white/60")}>
                                                    {token.address}
                                                </td>
                                                <td className={td}>{token.blockchain}</td>
                                                <td className={td}>{token.source || "—"}</td>
                                                <td className={cn(td, "text-right")}>
                                                    <div className="flex justify-end gap-1">
                                                        <Button
                                                            type="text"
                                                            onClick={() => openDrawerForEdit(token)}
                                                            icon={<Edit size={16} />}
                                                            className="!text-amber-200 hover:!bg-amber-500/10"
                                                        />
                                                        <Popconfirm
                                                            title="Удалить токен?"
                                                            description="Действие необратимо."
                                                            onConfirm={() => handleDelete(token.id)}
                                                            okText="Да"
                                                            cancelText="Нет"
                                                            okButtonProps={{ className: "!rounded-lg" }}
                                                            cancelButtonProps={{ className: "!rounded-lg" }}
                                                        >
                                                            <Button
                                                                type="text"
                                                                icon={<Trash2 size={16} />}
                                                                className="!text-rose-400 hover:!bg-rose-500/10"
                                                            />
                                                        </Popconfirm>
                                                    </div>
                                                </td>
                                            </motion.tr>
                                        ))
                                    )}
                                </tbody>
                            </AnimatePresence>
                        </table>
                    </div>
                </div>
            </section>

            <Drawer
                title={
                    <span className="font-semibold text-white/95">
                        {editingToken ? "Редактировать токен" : "Добавить токен"}
                    </span>
                }
                placement="right"
                width={480}
                onClose={() => setIsDrawerOpen(false)}
                open={isDrawerOpen}
                styles={{
                    header:  { background: '#070A12', borderColor: 'rgba(255,255,255,0.08)', color: '#fff' },
                    body:    { background: '#070A12', padding: '20px 24px' },
                    content: { background: '#070A12' },
                    mask:    { backdropFilter: 'blur(4px)' },
                }}
            >
                <motion.div
                    initial={{ x: 24, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: 24, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                >
                    <div className="flex flex-col gap-5">
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-medium uppercase tracking-wide text-white/40">
                                    Name
                                </label>
                                <input
                                    type="text"
                                    className={inputClass}
                                    value={manualForm.name}
                                    onChange={(e) => handleManualChange("name", e.target.value)}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-[11px] font-medium uppercase tracking-wide text-white/40">
                                    Symbol
                                </label>
                                <input
                                    type="text"
                                    className={inputClass}
                                    value={manualForm.symbol}
                                    onChange={(e) => handleManualChange("symbol", e.target.value)}
                                />
                            </div>
                            <div className="flex flex-col gap-1.5 md:col-span-2">
                                <label className="text-[11px] font-medium uppercase tracking-wide text-white/40">
                                    Address
                                </label>
                                <input
                                    type="text"
                                    className={inputClass}
                                    value={manualForm.address}
                                    onChange={(e) => handleManualChange("address", e.target.value)}
                                />
                            </div>
                            <div className="flex flex-col gap-2 md:col-span-2">
                                <label className="text-[11px] font-medium uppercase tracking-wide text-white/40">
                                    Blockchain
                                </label>
                                <div className="flex flex-wrap gap-1.5">
                                    {supportedChains.map((chain) => (
                                        <React.Fragment key={chain}>
                                            {chainPill(
                                                chain,
                                                manualForm.blockchain === chain,
                                                () => handleManualChange("blockchain", chain)
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                            {!editingToken && (
                                <div className="flex flex-col gap-1.5 md:col-span-2">
                                    <label className="text-[11px] font-medium uppercase tracking-wide text-white/40">
                                        Logo (файл)
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileChange}
                                        className="text-[13px] text-white/70 file:mr-3 file:rounded-lg file:border-0 file:bg-white/10 file:px-3 file:py-2 file:text-white/90 hover:file:bg-white/15"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2 border-t border-white/10 pt-4">
                            <MotionButton
                                type="primary"
                                onClick={handleManualSubmit}
                                className="!rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                Сохранить
                            </MotionButton>
                            <Button
                                onClick={() => setIsDrawerOpen(false)}
                                className="!rounded-xl !border-white/10 !bg-white/[0.06] !text-white/85 hover:!border-white/20 hover:!bg-white/10"
                            >
                                Отмена
                            </Button>
                        </div>
                    </div>
                </motion.div>
            </Drawer>

            <TokenCardOverlay visible={isVisible} onClose={() => setIsVisible(false)} foundTokenInDb={foundTokenInDb} />
        </div>
    );
}
